// Tournament Awards Show overlay
// Cycles through each player's awards (achievement-card style) and ends on champion

export type PlayerSlide = {
  name: string;
  avatar?: string;
  awards: Array<{ label: string; value?: string }>;
};

export function showTournamentAwardsShow(opts: {
  tournamentName: string;
  slides: PlayerSlide[];
  championName?: string;
  perSlideMs?: number; // default 3000
  onDone?: () => void;
  onSkip?: () => void;
}) {
  const perSlideMs = opts.perSlideMs ?? 3000;
  const slides = [...opts.slides];
  // Ensure champion (if present) is last
  if (opts.championName) {
    const idx = slides.findIndex(s => s.name === opts.championName);
    if (idx >= 0) {
      const [champ] = slides.splice(idx,1);
      slides.push(champ);
    }
  }
  if (!slides.length) { opts.onDone?.(); return; }

  // Root overlay
  const overlay = document.createElement('div');
  overlay.id = 'tourney-awards-overlay';
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:11000',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'background:rgba(0,0,0,0.65)',
    'backdrop-filter:blur(2px)',
  ].join(';');

  const wrap = document.createElement('div');
  wrap.style.cssText = [
    'background:rgba(10,10,14,0.85)',
    'border:1px solid rgba(255,255,255,0.12)',
    'border-radius:16px',
    'padding:18px 16px',
    'width:min(720px,92vw)',
    'box-shadow:0 18px 60px rgba(0,0,0,0.6)',
    'color:#fff',
  ].join(';');

  const header = document.createElement('div');
  header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;gap:8px';
  const title = document.createElement('div');
  title.textContent = `${opts.tournamentName} — Awards`;
  title.style.cssText = 'font-weight:800;letter-spacing:.02em;font-size:18px;opacity:.95';
  const close = document.createElement('button');
  close.type = 'button';
  close.textContent = 'Skip ×';
  close.style.cssText = 'background:#222;color:#fff;border:1px solid #444;border-radius:8px;padding:6px 10px;cursor:pointer;font-weight:700';
  header.appendChild(title);
  header.appendChild(close);

  const body = document.createElement('div');
  body.style.cssText = 'display:flex;flex-direction:column;gap:10px;align-items:center;justify-content:center;min-height:220px';

  wrap.appendChild(header);
  wrap.appendChild(body);
  overlay.appendChild(wrap);
  document.body.appendChild(overlay);

  let idx = 0;
  let timer: any = null;
  let disposed = false;

  const renderSlide = (s: PlayerSlide) => {
    body.innerHTML = '';
    const who = document.createElement('div');
    who.style.cssText = 'display:flex;align-items:center;gap:10px;font-weight:800;font-size:16px; margin-bottom: 10px;';
    const img = document.createElement('img');
    img.src = s.avatar || '';
    img.alt = s.name;
    img.style.cssText = 'width:28px;height:28px;border-radius:50%;object-fit:cover;display:' + (s.avatar ? 'inline-block':'none');
    const nm = document.createElement('span');
    nm.textContent = s.name;
    who.appendChild(img);
    who.appendChild(nm);
    body.appendChild(who);

    const grid = document.createElement('div');
    grid.style.cssText = [
      'display:grid',
      'grid-template-columns:repeat(2, minmax(220px, 1fr))',
      'gap:12px',
      'width:100%',
      'max-width:620px',
      'margin:0 auto',
      'justify-content:center',
      'justify-items:center',
      'align-items:stretch'
    ].join(';');
    const cards = s.awards && s.awards.length ? s.awards : [{ label:'Thanks for playing!', value:'' }];
    // If only one award, render a single centered column
    if (cards.length === 1) {
      grid.style.gridTemplateColumns = 'minmax(220px, 1fr)';
      grid.style.maxWidth = '320px';
    }
    for (const a of cards) {
      const card = document.createElement('div');
      card.className = 'achievement-card';
      const emoji = pickEmoji(a.label);
      card.innerHTML = `
        <div class="achievement-emoji">${emoji}</div>
        <div class="achievement-label">${escapeHtml(a.label)}</div>
        ${a.value ? `<div class="achievement-value">${escapeHtml(a.value)}</div>` : ''}
      `;
      grid.appendChild(card);
    }
    body.appendChild(grid);
  };

  const next = () => {
    if (disposed) return;
    const s = slides[idx];
    renderSlide(s);
    idx++;
    if (idx < slides.length) {
      timer = setTimeout(next, perSlideMs);
    } else {
      timer = setTimeout(() => done(), perSlideMs);
    }
  };

  const done = () => {
    if (disposed) return;
    disposed = true;
    try { if (timer) clearTimeout(timer); } catch {}
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 250ms ease';
    setTimeout(() => {
      if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
      opts.onDone?.();
    }, 260);
  };

  const skip = () => {
    if (disposed) return;
    disposed = true;
    try { if (timer) clearTimeout(timer); } catch {}
    if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
    opts.onSkip?.();
  };

  close.onclick = skip;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) skip(); });

  // Helpers
  function escapeHtml(s: string) {
    return s.replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;'} as any)[c]);
  }
  function pickEmoji(label: string): string {
    const normalized = label.toLowerCase();
    if (normalized.includes('winner') || normalized.includes('champion')) return '🏆';
    if (normalized.includes('skrollr')) return '🎯';
    if (normalized.includes('fast')) return '⚡';
    if (normalized.includes('slow')) return '🐢';
    if (normalized.includes('pop')) return '🎈';
    if (normalized.includes('orbit')) return '🪐';
    if (normalized.includes('bust')) return '💥';
    if (normalized.includes('streak')) return '🔥';
    if (normalized.includes('efficiency') || normalized.includes('ppr')) return '📈';
    if (normalized.includes('turn') || normalized.includes('score')) return '🎳';
    return '🎖️';
  }

  // Start
  next();
}
